import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  Bed,
  Bath,
  Toilet,
  Maximize,
  MapPin,
  Phone,
  Share2,
  Heart,
  ChevronLeft,
  ChevronRight,
  X,
  Home,
  Calendar,
  Eye,
  ImageOff,
  AlertTriangle
} from 'lucide-react'
import ChatWidget from '@/components/ChatWidget'
import { toast } from 'react-toastify'
import { useAnalytics } from '@/hooks/useAnalytics'

interface Property {
  id: string
  title: string
  description: string
  price: number
  currency: string
  property_type: string
  bedrooms?: number
  bathrooms?: number
  half_bathrooms?: number
  area_m2?: number
  zone: string
  address?: string
  images: string[]
  featured_image?: string
  views_count: number
  purpose: string
  status: string
  published_at: string
  virtual_tour_url?: string
  seo_title: string
  latitude?: number
  longitude?: number
}

interface Agent {
  id: string
  name: string
  phone: string
  email: string
  bio: string
  avatar_url?: string
  subdomain: string
  template: string
  specialties?: string[]
}

const PropertyDetailPage: React.FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>()
  const navigate = useNavigate()
  const subdomain = window.location.hostname.split('.')[0]

  const [property, setProperty] = useState<Property | null>(null)
  const [agent, setAgent] = useState<Agent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isBlocked, setIsBlocked] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [showGallery, setShowGallery] = useState(false)
  const [isFavorite, setIsFavorite] = useState(false)

  // Analytics tracking - NO pasar propertyId aquí (es slug, no UUID)
  // trackPageView se llama manualmente después de obtener el UUID real
  const {
    trackPageView,
    trackPropertyView,
    trackPropertyImageClick,
    trackPropertyContact
  } = useAnalytics({
    agentSubdomain: subdomain,
    pageType: 'property'
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch propiedad por seo_title
        const propertyResponse = await fetch(`/api/agents/subdomain/${subdomain}/properties/${propertyId}`)
        const propertyData = await propertyResponse.json()

        // Verificar si está bloqueado
        if (propertyData.blocked) {
          setIsBlocked(true)
          return
        }

        if (propertyResponse.status === 410) {
          if (propertyData.status === 'sold') throw new Error('Esta propiedad ya fue vendida')
          if (propertyData.status === 'rented') throw new Error('Esta propiedad ya fue rentada')
          throw new Error('Esta propiedad ya no está disponible')
        }
        if (!propertyResponse.ok) throw new Error('Propiedad no encontrada')

        // Fetch datos del agente
        const agentResponse = await fetch(`/api/agents/subdomain/${subdomain}`)
        const agentData = await agentResponse.json()

        // Verificar si está bloqueado
        if (agentData.blocked) {
          setIsBlocked(true)
          return
        }

        if (!agentResponse.ok) throw new Error('Agente no encontrado')

        setProperty(propertyData)
        setAgent(agentData)

        // Track page view con el UUID real de la propiedad (no el slug del URL)
        trackPageView(propertyData.id)

        const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
        setIsFavorite(favorites.includes(propertyId))
      } catch (err: any) {
        console.error('Error fetching property:', err)
        setError(err.message || 'Error al cargar la propiedad')
      } finally {
        setLoading(false)
      }
    }

    if (propertyId && subdomain) {
      fetchData()
    }
  }, [propertyId, subdomain])

  const formatPrice = (price: number, currency: string) => {
    if (price >= 1000000) {
      return `$${(price / 1000000).toFixed(2)}M ${currency}`
    }
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }

  const handleShare = async () => {
    const url = window.location.href
    // Track share interaction
    if (property) {
      trackPropertyContact(property.id)
    }
    if (navigator.share) {
      try {
        await navigator.share({
          title: property?.title,
          text: property?.description,
          url: url
        })
      } catch (err) {
        console.log('Error sharing:', err)
      }
    } else {
      navigator.clipboard.writeText(url)
      toast.success('Link copiado al portapapeles')
    }
  }

  const toggleFavorite = () => {
    if (!propertyId) return

    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
    if (isFavorite) {
      const updated = favorites.filter((id: string) => id !== propertyId)
      localStorage.setItem('favorites', JSON.stringify(updated))
    } else {
      favorites.push(propertyId)
      localStorage.setItem('favorites', JSON.stringify(favorites))
    }
    setIsFavorite(!isFavorite)
  }

  const nextImage = () => {
    if (!property) return
    const allImages = (property.images?.length || 0) > 0 ? property.images : (property.featured_image ? [property.featured_image] : [])
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length)
  }

  const prevImage = () => {
    if (!property) return
    const allImages = (property.images?.length || 0) > 0 ? property.images : (property.featured_image ? [property.featured_image] : [])
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length)
  }

  // Handler para click en imagen (con tracking)
  const handleImageClick = (index?: number) => {
    if (property) {
      trackPropertyImageClick(property.id, index ?? currentImageIndex)
    }
    setShowGallery(true)
  }

  // Handler para click en contacto (tel/WhatsApp)
  const handleContactClick = (contactType: 'phone' | 'whatsapp') => {
    if (property) {
      trackPropertyContact(property.id)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando propiedad...</p>
        </div>
      </div>
    )
  }

  // Página de sitio bloqueado
  if (isBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center p-8 max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">
            Sitio temporalmente no disponible
          </h2>
          <p className="text-slate-400 mb-6">
            Este sitio se encuentra en mantenimiento. Por favor, intenta más tarde.
          </p>
          <a
            href="https://innovagent.tech"
            className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Ir a InnovAgent
          </a>
        </div>
      </div>
    )
  }

  if (error || !property || !agent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="text-center p-8">
          <Home className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-2xl text-gray-600 mb-2">{error || 'Propiedad no encontrada'}</h2>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    )
  }

  const allImages = (property.images?.length || 0) > 0 ? property.images : (property.featured_image ? [property.featured_image] : [])
  const hasImages = allImages.length > 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-100">
      <div className="bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span className="font-medium">Volver</span>
            </button>
            <div className="flex items-center gap-4">
              <button onClick={toggleFavorite} className={`p-2 rounded-full transition-colors ${isFavorite ? 'bg-red-100 text-red-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
              <button onClick={handleShare} className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
                <Share2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {hasImages ? (
        <div className="relative h-[60vh] bg-black">
          <img src={allImages[currentImageIndex]} alt={property.title} className="w-full h-full object-contain cursor-pointer" onClick={() => handleImageClick(currentImageIndex)} />
          {allImages.length > 1 && (
            <>
              <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all">
                <ChevronLeft className="h-6 w-6 text-gray-800" />
              </button>
              <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all">
                <ChevronRight className="h-6 w-6 text-gray-800" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {allImages.map((_, index) => (
                  <button key={index} onClick={() => setCurrentImageIndex(index)} className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex ? 'bg-white w-8' : 'bg-white/50'}`} />
                ))}
              </div>
            </>
          )}
          <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full font-semibold text-lg shadow-lg">
            {formatPrice(property.price, property.currency)}
          </div>
        </div>
      ) : (
        <div className="relative h-[60vh] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <div className="text-center">
            <ImageOff className="h-24 w-24 mx-auto mb-4 text-gray-400" />
            <p className="text-xl text-gray-600 font-medium">Sin imágenes disponibles</p>
            <p className="text-gray-500 mt-2">Contacta al agente para más información</p>
          </div>
          <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full font-semibold text-lg shadow-lg">
            {formatPrice(property.price, property.currency)}
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.title}</h1>
                  <div className="flex items-center text-gray-600 mb-3">
                    <MapPin className="h-5 w-5 mr-2" />
                    <span className="text-lg">{property.zone}</span>
                  </div>
                  {property.address && <p className="text-gray-500">{property.address}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-t border-b border-gray-200">
                {property.bedrooms && (
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-lg"><Bed className="h-6 w-6 text-blue-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{property.bedrooms}</p>
                      <p className="text-sm text-gray-600">Recámaras</p>
                    </div>
                  </div>
                )}
                {property.bathrooms && (
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-100 rounded-lg"><Bath className="h-6 w-6 text-purple-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{property.bathrooms}</p>
                      <p className="text-sm text-gray-600">Baños</p>
                    </div>
                  </div>
                )}
                {property.half_bathrooms > 0 && (
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-orange-100 rounded-lg"><Toilet className="h-6 w-6 text-orange-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{property.half_bathrooms}</p>
                      <p className="text-sm text-gray-600">{property.half_bathrooms === 1 ? 'Medio baño' : 'Medios baños'}</p>
                    </div>
                  </div>
                )}
                {property.area_m2 && (
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-100 rounded-lg"><Maximize className="h-6 w-6 text-green-600" /></div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{property.area_m2}</p>
                      <p className="text-sm text-gray-600">m²</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gray-100 rounded-lg"><Eye className="h-6 w-6 text-gray-600" /></div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{property.views_count}</p>
                    <p className="text-sm text-gray-600">Vistas</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-6">
                <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">{property.property_type}</span>
                <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium capitalize">{property.purpose}</span>
                <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {new Date(property.published_at).toLocaleDateString('es-MX')}
                </span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Descripción</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{property.description}</p>
            </motion.div>

            {property.virtual_tour_url && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Tour Virtual</h2>
                <a href={property.virtual_tour_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                  <Home className="h-5 w-5" />
                  Ver Tour Virtual
                </a>
              </motion.div>
            )}
          </div>

            {property.latitude && property.longitude && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Ubicación</h2>
                <div className="rounded-xl overflow-hidden">
                  <iframe
                    title="Ubicación de la propiedad"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${Number(property.latitude).toFixed(2)},${Number(property.longitude).toFixed(2)}&zoom=13`}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Ubicación aproximada de la propiedad
                </p>
              </motion.div>
            )}

          <div className="lg:col-span-1">
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Contactar al Agente</h3>
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-200">
                {agent.avatar_url && <img src={agent.avatar_url} alt={agent.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-gray-200" />}
                <div>
                  <p className="font-semibold text-gray-900 text-lg">{agent.name}</p>
                  <p className="text-gray-600 text-sm line-clamp-2">{agent.bio}</p>
                </div>
              </div>
              <div className="space-y-3">
                <a
                  href={`tel:${agent.phone}`}
                  onClick={() => handleContactClick('phone')}
                  className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors shadow-md"
                >
                  <Phone className="h-5 w-5" />
                  <span>Llamar ahora</span>
                </a>
                <a
                  href={`https://wa.me/${agent.phone.replace(/\D/g, '')}?text=Hola, me interesa la propiedad: ${property.title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleContactClick('whatsapp')}
                  className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-lg font-medium transition-colors shadow-md"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>WhatsApp</span>
                </a>
              </div>
              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 font-medium">Precio</span>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{formatPrice(property.price, property.currency)}</p>
                    <p className="text-sm text-gray-600 capitalize">{property.purpose}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showGallery && hasImages && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black z-50 flex items-center justify-center" onClick={() => setShowGallery(false)}>
            <button onClick={() => setShowGallery(false)} className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors">
              <X className="h-6 w-6 text-white" />
            </button>
            <img src={allImages[currentImageIndex]} alt={property.title} className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
            {allImages.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors">
                  <ChevronLeft className="h-8 w-8 text-white" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors">
                  <ChevronRight className="h-8 w-8 text-white" />
                </button>
              </>
            )}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
              {currentImageIndex + 1} / {allImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {agent && property && <ChatWidget agentName={agent.name} agentSubdomain={subdomain} agentAvatar={agent.avatar_url} autoOpen={false} propertyId={property.id} />}
    </div>
  )
}

export default PropertyDetailPage
